import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

import type { NetworkErrorType } from '../../../utilities/errors/networkErrorType';

import { classifyNetworkError } from './classifyNetworkError';
import { readEnableRetry, readRequestMetadata } from './types';

export const MAX_TRANSPORT_RETRIES = 3;

/** Exponential backoff delays (ms) before retry attempts 1, 2, and 3. */
export const TRANSPORT_RETRY_DELAYS_MS = [500, 1000, 2000] as const;

const RETRYABLE_NETWORK_TYPES = new Set<NetworkErrorType>([
  'timeout',
  'server_unreachable',
  'server_error',
]);

const RECORDABLE_FAILURE_TYPES = new Set<NetworkErrorType>([
  ...RETRYABLE_NETWORK_TYPES,
  'offline',
]);

const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

const RETRYABLE_HTTP_STATUSES = new Set([502, 503, 504]);

function hasRetryableHttpResponse(error: AxiosError): boolean {
  const status = error.response?.status;
  return typeof status === 'number' && RETRYABLE_HTTP_STATUSES.has(status);
}

/**
 * Delay before the next transport retry attempt.
 */
export function getTransportRetryDelay(retryCount: number): number {
  const index = Math.min(retryCount, TRANSPORT_RETRY_DELAYS_MS.length - 1);
  return TRANSPORT_RETRY_DELAYS_MS[index] ?? TRANSPORT_RETRY_DELAYS_MS[0];
}

/**
 * Returns `true` when the HTTP method is safe to retry at the transport layer.
 */
export function isIdempotentMethod(method: string | undefined): boolean {
  return IDEMPOTENT_METHODS.has((method ?? 'GET').toUpperCase());
}

/**
 * Returns `true` when a failed request should be retried by the axios interceptor.
 * Retries idempotent reads automatically; mutating requests require `enableRetry: true`.
 */
export function shouldRetryTransportRequest(
  config: InternalAxiosRequestConfig,
  error: AxiosError,
): boolean {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return false;
  }

  const retryCount = readRequestMetadata(config)?.retryCount ?? 0;
  if (retryCount >= MAX_TRANSPORT_RETRIES) {
    return false;
  }

  const methodAllowed =
    isIdempotentMethod(config.method) || readEnableRetry(config);
  if (!methodAllowed) {
    return false;
  }

  if (error.response && !hasRetryableHttpResponse(error)) {
    return false;
  }

  const networkType = classifyNetworkError(error);
  return RETRYABLE_NETWORK_TYPES.has(networkType);
}

/**
 * Returns `true` when a failed request should count toward unstable-connection UX.
 * Application errors (4xx) and explicit cancellations are excluded.
 */
export function shouldRecordNetworkFailure(error: unknown): boolean {
  if (axios.isCancel(error)) {
    return false;
  }

  if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
    return false;
  }

  return RECORDABLE_FAILURE_TYPES.has(classifyNetworkError(error));
}
